import React, { useEffect, useRef, useState } from 'react';
import AsyncSelect from 'react-select/async';
import {
  getClients as getClientsApi,
  getClientById as getClientByIdApi,
} from '../../../../helpers/fakebackend_helper';

const SEARCH_DEBOUNCE_MS = 400;
const PAGE_SIZE = 20;

const clientLabel = (client) => {
  if (client.buyer_type === 'LEGAL') {
    return client.legal_company_name || client.name;
  }
  return `${client.name || ''} ${client.surname || ''}`.trim();
};

const toOption = (client) => ({
  value: client.id,
  label: clientLabel(client),
  client,
});

const formatOptionLabel = ({ label, client }) => (
  <div className="d-flex justify-content-between align-items-center gap-2">
    <span>{label}</span>
    {client && (
      <span className="text-muted small">
        {client.buyer_type === 'LEGAL' ? 'Legal' : 'Natural'}
        {client.mobile_phone_number ? ` · ${client.mobile_phone_number}` : ''}
      </span>
    )}
  </div>
);

/**
 * Searchable client picker for the contract wizard. Queries /clients/
 * directly (both buyer types, server-side search) instead of reading the
 * Redux client list, which only ever holds the one page — and one buyer
 * type — the Clients page last loaded.
 */
const ClientSelect = ({ value, onChange, onBlur, invalid }) => {
  const [selected, setSelected] = useState(null);
  const debounceRef = useRef(null);

  // Resolve a client id set from outside (e.g. a client just created inside
  // the wizard) into a labelled option.
  useEffect(() => {
    if (!value) {
      setSelected(null);
      return;
    }
    if (selected && String(selected.value) === String(value)) return;

    let cancelled = false;
    getClientByIdApi(value)
      .then((res) => {
        const client = res?.data || res;
        if (!cancelled && client?.id) setSelected(toOption(client));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [value]);

  const loadOptions = (inputValue, callback) => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const params = { ordering: 'name', page: 1, page_size: PAGE_SIZE };
        const search = inputValue.trim();
        if (search) params.search = search;
        const res = await getClientsApi(params);
        const results = Array.isArray(res?.results) ? res.results : [];
        callback(results.map(toOption));
      } catch (err) {
        console.error('Failed to search clients:', err);
        callback([]);
      }
    }, SEARCH_DEBOUNCE_MS);
  };

  return (
    <AsyncSelect
      inputId="client-field"
      name="client"
      classNamePrefix="react-select"
      className={invalid ? 'is-invalid' : ''}
      cacheOptions
      defaultOptions
      isClearable
      loadOptions={loadOptions}
      value={selected}
      formatOptionLabel={formatOptionLabel}
      placeholder="Search a client by name, phone, email or company..."
      noOptionsMessage={({ inputValue }) =>
        inputValue ? 'No clients found' : 'Type to search clients'
      }
      loadingMessage={() => 'Searching...'}
      onChange={(option) => {
        setSelected(option);
        onChange(option ? option.value : '');
      }}
      onBlur={onBlur}
      styles={
        invalid
          ? { control: (base) => ({ ...base, borderColor: 'var(--vz-form-invalid-border-color, #f06548)' }) }
          : undefined
      }
    />
  );
};

export default ClientSelect;
